// styleProps.ts - Utility for handling styled-system style props
import { getColor, convertToCSSSize } from "../theme/theme";
import { SpaceScale, ColorKey, ThemeMode } from "../theme/theme.types";

// Display props
export interface DisplayProps {
  display?:
    | "block"
    | "inline"
    | "inline-block"
    | "flex"
    | "inline-flex"
    | "grid"
    | "inline-grid"
    | "none";
}

// Margin props
export interface MarginProps {
  m?: SpaceScale | number | string;
  mt?: SpaceScale | number | string;
  mr?: SpaceScale | number | string;
  mb?: SpaceScale | number | string;
  ml?: SpaceScale | number | string;
  mx?: SpaceScale | number | string;
  my?: SpaceScale | number | string;
}

// Padding props
export interface PaddingProps {
  p?: SpaceScale | number | string;
  pt?: SpaceScale | number | string;
  pr?: SpaceScale | number | string;
  pb?: SpaceScale | number | string;
  pl?: SpaceScale | number | string;
  px?: SpaceScale | number | string;
  py?: SpaceScale | number | string;
}

// Layout props
export interface LayoutProps {
  width?: SpaceScale | string | number;
  height?: SpaceScale | string | number;
  minWidth?: SpaceScale | string | number;
  maxWidth?: SpaceScale | string | number;
  minHeight?: SpaceScale | string | number;
  maxHeight?: SpaceScale | string | number;
  size?: SpaceScale | string | number; // Sets both width and height
  overflow?: "visible" | "hidden" | "scroll" | "auto";
  overflowX?: "visible" | "hidden" | "scroll" | "auto";
  overflowY?: "visible" | "hidden" | "scroll" | "auto";
}

// Flexbox props (without display property)
export interface FlexboxProps {
  alignItems?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
  justifyContent?:
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly";
  flexDirection?: "row" | "column" | "row-reverse" | "column-reverse";
  flexWrap?: "nowrap" | "wrap" | "wrap-reverse";
  flex?: string | number;
  flexGrow?: number;
  flexShrink?: number;
  flexBasis?: SpaceScale | number | string;
  gap?: SpaceScale | number | string;
  rowGap?: SpaceScale | number | string;
  columnGap?: SpaceScale | number | string;
}

// Grid props (without display property)
export interface GridProps {
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  gridTemplateAreas?: string;
  gridArea?: string;
  gridAutoFlow?: "row" | "column" | "row dense" | "column dense";
  gridAutoRows?: string;
  gridAutoColumns?: string;
  gridColumn?: string;
  gridRow?: string;
}

// Position props
export interface PositionProps {
  position?: "static" | "relative" | "absolute" | "fixed" | "sticky";
  top?: SpaceScale | string | number;
  right?: SpaceScale | string | number;
  bottom?: SpaceScale | string | number;
  left?: SpaceScale | string | number;
  zIndex?: number;
}

// Border props
export interface BorderProps {
  border?: string;
  borderTop?: string;
  borderRight?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderColor?: ColorKey;
  borderRadius?: number | string;
}

// Color props
export interface ColorProps {
  bg?: ColorKey;
  backgroundColor?: ColorKey;
  color?: ColorKey;
  opacity?: number;
}

// Shadow props
export interface ShadowProps {
  boxShadow?: string;
  textShadow?: string;
}

// Interactivity props
export interface InteractivityProps {
  cursor?:
    | "default"
    | "pointer"
    | "text"
    | "not-allowed"
    | "help"
    | "move"
    | "grab"
    | "grabbing"
    | "zoom-in"
    | "zoom-out";
  userSelect?: "none" | "auto" | "text" | "contain" | "all";
  resize?: "none" | "both" | "horizontal" | "vertical";
  transition?: string;
  outline?: string;
  outlineOffset?: string | number;
}

// Typography props - Added to styleProps system
export interface TypographyProps {
  fontFamily?: string;
  fontSize?: number | string;
  fontWeight?: number | string;
  lineHeight?: number | string;
  letterSpacing?: number | string;
  textAlign?: "left" | "center" | "right" | "justify";
  textTransform?: "none" | "capitalize" | "uppercase" | "lowercase";
  textDecoration?: "none" | "underline" | "line-through" | "overline";
  fontStyle?: "normal" | "italic" | "oblique";
  textOverflow?: "ellipsis" | "clip";
  lineClamp?: number;
  whiteSpace?:
    | "normal"
    | "nowrap"
    | "pre"
    | "pre-wrap"
    | "pre-line"
    | "break-spaces";
  wordBreak?: "normal" | "break-all" | "break-word" | "keep-all";
  wordWrap?: "normal" | "break-word";
}

// Add style prop for inline CSS
export interface StylesProps {
  style?: React.CSSProperties;
}

// Combined props
export type StyleProps = DisplayProps &
  MarginProps &
  PaddingProps &
  LayoutProps &
  FlexboxProps &
  GridProps &
  PositionProps &
  BorderProps &
  ColorProps &
  ShadowProps &
  InteractivityProps &
  TypographyProps &
  StylesProps;

/**
 * Resolves display properties
 */
export const getDisplayProps = (
  props: DisplayProps
): Record<string, string> => {
  return {
    display: props.display || "block", // Always provide 'block' as fallback
  };
};

/**
 * Resolves margin properties from style props
 */
export const getMarginProps = (props: MarginProps): Record<string, string> => {
  const result: Record<string, string> = {};

  if (props.m !== undefined) {
    const margin = convertToCSSSize(props.m);
    if (margin !== undefined) result.margin = margin;
  }
  if (props.mt !== undefined) {
    const marginTop = convertToCSSSize(props.mt);
    if (marginTop !== undefined) result.marginTop = marginTop;
  }
  if (props.mr !== undefined) {
    const marginRight = convertToCSSSize(props.mr);
    if (marginRight !== undefined) result.marginRight = marginRight;
  }
  if (props.mb !== undefined) {
    const marginBottom = convertToCSSSize(props.mb);
    if (marginBottom !== undefined) result.marginBottom = marginBottom;
  }
  if (props.ml !== undefined) {
    const marginLeft = convertToCSSSize(props.ml);
    if (marginLeft !== undefined) result.marginLeft = marginLeft;
  }

  // Handle mx and my shorthands
  if (props.mx !== undefined) {
    const marginX = convertToCSSSize(props.mx);
    if (marginX !== undefined) {
      result.marginLeft = marginX;
      result.marginRight = marginX;
    }
  }

  if (props.my !== undefined) {
    const marginY = convertToCSSSize(props.my);
    if (marginY !== undefined) {
      result.marginTop = marginY;
      result.marginBottom = marginY;
    }
  }

  return result;
};

/**
 * Resolves padding properties from style props
 */
export const getPaddingProps = (
  props: PaddingProps
): Record<string, string> => {
  const result: Record<string, string> = {};

  if (props.p !== undefined) {
    const padding = convertToCSSSize(props.p);
    if (padding !== undefined) result.padding = padding;
  }
  if (props.pt !== undefined) {
    const paddingTop = convertToCSSSize(props.pt);
    if (paddingTop !== undefined) result.paddingTop = paddingTop;
  }
  if (props.pr !== undefined) {
    const paddingRight = convertToCSSSize(props.pr);
    if (paddingRight !== undefined) result.paddingRight = paddingRight;
  }
  if (props.pb !== undefined) {
    const paddingBottom = convertToCSSSize(props.pb);
    if (paddingBottom !== undefined) result.paddingBottom = paddingBottom;
  }
  if (props.pl !== undefined) {
    const paddingLeft = convertToCSSSize(props.pl);
    if (paddingLeft !== undefined) result.paddingLeft = paddingLeft;
  }

  // Handle px and py shorthands
  if (props.px !== undefined) {
    const paddingX = convertToCSSSize(props.px);
    if (paddingX !== undefined) {
      result.paddingLeft = paddingX;
      result.paddingRight = paddingX;
    }
  }

  if (props.py !== undefined) {
    const paddingY = convertToCSSSize(props.py);
    if (paddingY !== undefined) {
      result.paddingTop = paddingY;
      result.paddingBottom = paddingY;
    }
  }
  return result;
};

/**
 * Resolves color properties from style props
 * @param props - Color properties
 * @param theme - Optional theme mode for semantic token resolution
 */
export const getColorProps = (
  props: ColorProps,
  theme?: ThemeMode
): Record<string, string> => {
  const result: Record<string, string> = {};

  if (props.bg !== undefined)
    result.backgroundColor = getColor(props.bg, theme);
  if (props.backgroundColor !== undefined)
    result.backgroundColor = getColor(props.backgroundColor, theme);
  if (props.color !== undefined) result.color = getColor(props.color, theme);
  if (props.opacity !== undefined) result.opacity = String(props.opacity);

  return result;
};

/**
 * Resolves typography properties from style props
 */
export const getTypographyProps = (
  props: TypographyProps
): Record<string, string> => {
  const result: Record<string, string> = {};

  // Font properties
  if (props.fontFamily !== undefined) result.fontFamily = props.fontFamily;

  if (props.fontSize !== undefined) {
    result.fontSize =
      typeof props.fontSize === "number"
        ? `${props.fontSize}px`
        : props.fontSize;
  }

  if (props.fontWeight !== undefined) {
    result.fontWeight = String(props.fontWeight);
  }

  if (props.lineHeight !== undefined) {
    result.lineHeight =
      typeof props.lineHeight === "number"
        ? `${props.lineHeight}px`
        : props.lineHeight;
  }

  if (props.letterSpacing !== undefined) {
    result.letterSpacing =
      typeof props.letterSpacing === "number"
        ? `${props.letterSpacing}px`
        : props.letterSpacing;
  }

  // Text alignment and styling
  if (props.textAlign !== undefined) result.textAlign = props.textAlign;
  if (props.textTransform !== undefined)
    result.textTransform = props.textTransform;
  if (props.textDecoration !== undefined)
    result.textDecoration = props.textDecoration;
  if (props.fontStyle !== undefined) result.fontStyle = props.fontStyle;
  if (props.whiteSpace !== undefined) result.whiteSpace = props.whiteSpace;
  if (props.wordBreak !== undefined) result.wordBreak = props.wordBreak;
  if (props.wordWrap !== undefined) result.wordWrap = props.wordWrap;

  // Text overflow handling
  if (props.textOverflow !== undefined) {
    result.overflow = "hidden"; // Always set overflow hidden for textOverflow

    if (props.lineClamp !== undefined) {
      // Handle multi-line truncation based on overflow type
      if (props.textOverflow) {
        // For ellipsis (default), use WebKit line clamp
        result.display = "-webkit-box";
        result.WebkitLineClamp = String(props.lineClamp);
        result.WebkitBoxOrient = "vertical";
        result.textOverflow = props.textOverflow;
        result.whiteSpace = props.whiteSpace || "break-spaces";
        if (props.textOverflow === "clip") {
          result.WebkitLineClamp = "unset";
          result.maxHeight = `${
            parseInt(result.lineHeight || "0") * props.lineClamp
          }px`;
        }
      }
    } else {
      // Single-line truncation
      result.textOverflow = props.textOverflow;
      if (props.whiteSpace === undefined) {
        result.whiteSpace = "nowrap";
      }
    }
  }

  return result;
};
